/**
 * App - top-level Routes. Per CHAP-78 (Phase 1C #44).
 *
 * Routes:
 *   /                                → redirect to /c/omega-zeta (v1 dev default)
 *   /c/:slug                         → tenant home (Overview)
 *   /c/:slug/summary                 → ExecutiveSummary
 *   /c/:slug/kpis                    → KPIs
 *   /c/:slug/financial               → Financial
 *   /c/:slug/analytics/*             → Analytics (sub-routing in its own ticket)
 *   /c/:slug/voting                  → Voting
 *   /c/:slug/okrs                    → OKRs
 *   /c/:slug/renderings              → Renderings
 *   /c/:slug/alumni                  → Alumni Reconciliation
 *   /c/:slug/document-lineage        → Version History
 *   /c/:slug/lcaef                   → LCAEF Campaign (flag: lcaef_enabled)
 *   /c/:slug/dataroom                → Data Room
 *   /c/:slug/feedback                → Feedback
 *   /c/:slug/discovery               → Discovery sender console (CHAP-593)
 *   /admin                           → Admin home (cross-tenant)
 *   /admin/...                       → Admin sub-routes (placeholder)
 *   *                                → 404
 *
 * Phase 1C #45..#55 (CHAP-79..89) replace each ComingSoon stub with
 * the real ported component from omega-club.
 */
import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/layout/Layout.js';
import { ProtectedRoute } from './components/ProtectedRoute.js';
import { RestrictedTierGate, ConfidentialTierGate } from './components/RoleGuard.js';
import { TenantLoading } from './components/TenantDataStates.js';
import { useFeatureFlagBoolean } from './hooks/useFeatureFlag.js';
import ExecutiveSummary from './components/ExecutiveSummary.js';
import Login from './pages/Login.js';
import Redeem from './pages/Redeem.js';
import Overview from './pages/Overview.js';
import KPIs from './pages/KPIs.js';
import RebuildVsRenovate from './pages/RebuildVsRenovate.js';
import Competitive from './pages/Competitive.js';
import Parking from './pages/Parking.js';
import EntityStructure from './pages/EntityStructure.js';
import Financial from './pages/Financial.js';
import Fundraising from './pages/Fundraising.js';
import OKRs from './pages/OKRs.js';
import Voting from './pages/Voting.js';
import Renderings from './pages/Renderings.js';
import DocumentLineage from './pages/DocumentLineage.js';
// CHAP-491 — LCAEFCampaign is the one page whose CONTENT belongs to a single
// tenant: it is a read of Omega Zeta's Foundation agreement, naming that
// contract's fee, its three defects and the vendor. Every other route here is
// statically imported, which would put that analysis into the bundle served to
// every tenant. Lazy, so its chunk is fetched only by a tenant whose
// `lcaef_enabled` flag is on.
const LCAEFCampaign = lazy(() => import('./pages/LCAEFCampaign.js'));
import DataRoom from './pages/DataRoom.js';
import Feedback from './pages/Feedback.js';
import ComingSoon from './pages/ComingSoon.js';
import NotFound from './pages/NotFound.js';
import Privacy from './pages/legal/Privacy.js';
import Terms from './pages/legal/Terms.js';
import Cookies from './pages/legal/Cookies.js';
import AdminHealth from './pages/admin/AdminHealth.js';
import AdminMetrics from './pages/admin/AdminMetrics.js';
import AdminBilling from './pages/admin/AdminBilling.js';
import AdminTenantNew from './pages/admin/AdminTenantNew.js';
import TenantSettings from './pages/TenantSettings.js';
import AdminAuditLog from './pages/admin/AdminAuditLog.js';
import AdminUsers from './pages/admin/AdminUsers.js';
import AdminFeatureFlags from './pages/admin/AdminFeatureFlags.js';
import AdminDataRoomQueue from './pages/admin/AdminDataRoomQueue.js';
import AlumniReconciliation from './pages/AlumniReconciliation.js';
import DonorPipeline from './pages/DonorPipeline.js';
import ParticipateLeads from './pages/ParticipateLeads.js';
import Discovery from './pages/Discovery.js';
import Cabinet from './pages/Cabinet.js';
import CapitalScenarios from './pages/CapitalScenarios.js';
import Meetings from './pages/Meetings.js';


/**
 * CHAP-491 — LCAEF is gated on a per-tenant feature flag as well as the tier,
 * because its content is one chapter's contract rather than a product feature.
 * A tenant without the flag gets the ordinary 404, so the route is
 * indistinguishable from one that does not exist.
 *
 * `useFeatureFlagBoolean` returns false while the flag request is in flight, so
 * the gate is closed by default and the page never flashes before hiding.
 */
function LcaefRoute() {
  const enabled = useFeatureFlagBoolean('lcaef_enabled');
  if (!enabled) return <NotFound />;
  return (
    <Suspense fallback={<TenantLoading />}>
      <LCAEFCampaign />
    </Suspense>
  );
}

export function App() {
  return (
    <Routes>
      {/* Root redirect - v1 default tenant */}
      <Route path="/" element={<Navigate to="/c/omega-zeta" replace />} />

      {/* Auth (un-gated) - CHAP-284 */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/redeem" element={<Redeem />} />

      {/* Authenticated app - ProtectedRoute redirects to /login when signed out */}
      <Route element={<ProtectedRoute />}>
      {/* Customer routes (tenant-scoped) */}
      <Route path="/c/:slug" element={<Layout variant="customer" />}>
        <Route index element={<Overview />} />
        <Route path="summary"    element={<ExecutiveSummary />} />
        <Route path="kpis"       element={<KPIs />} />
        <Route path="financial"  element={<Financial />} />
        {/* Analytics sub-routes - full Analytics shell + sub-nav lands in a follow-up; for now each sub-tab routes directly. */}
        <Route path="analytics" element={<Navigate to="fundraising" replace />} />
        {/* CHAP-87 — scheme_comparisons is restricted on the same spec line as
            competitive_chapters / housing_economics / parking_revenue
            (data-classification.md:186-187); it was the only member of that line
            left ungated. */}
        <Route path="analytics/rebuild"     element={<RestrictedTierGate label="Rebuild vs renovate"><RebuildVsRenovate /></RestrictedTierGate>} />
        {/* CHAP-86 / CHAP-82 — restricted (voter+) per data-classification.md */}
        <Route path="analytics/competitive" element={<RestrictedTierGate label="Competitive analysis"><Competitive /></RestrictedTierGate>} />
        {/* CHAP-83 — Fundraising renders donorCapacityTiers + giftChart, which
            data-classification.md:107-108 rates CONFIDENTIAL ("donor capacity
            ratings are sensitive personal financial estimates") = board+, one
            tier stricter than the restricted pages around it. */}
        <Route path="analytics/fundraising" element={<ConfidentialTierGate label="Fundraising"><Fundraising /></ConfidentialTierGate>} />
        <Route path="analytics/entity"      element={<RestrictedTierGate label="The entity structure"><EntityStructure /></RestrictedTierGate>} />
        <Route path="analytics/scenarios"   element={<RestrictedTierGate label="Capital structure scenarios"><CapitalScenarios /></RestrictedTierGate>} />
        <Route path="analytics/parking"     element={<RestrictedTierGate label="Parking analysis"><Parking /></RestrictedTierGate>} />
        <Route path="voting"     element={<Voting />} />
        {/* CHAP-84 — okr_initiatives + okr_key_results are restricted (voter+)
            per data-classification.md:104-105. */}
        <Route path="okrs"       element={<RestrictedTierGate label="OKR tracking"><OKRs /></RestrictedTierGate>} />
        {/* CHAP-89 - architectural renderings are `restricted` per
            data-classification.md ("architectural deliverables"). */}
        <Route path="renderings" element={<RestrictedTierGate label="Renderings"><Renderings /></RestrictedTierGate>} />
        {/* CHAP-491 - Version History is `restricted` for the same reason
            Renderings is: it names internal model disagreements and the
            counsel engagement price, which are not customer-tier content. */}
        <Route path="document-lineage" element={<RestrictedTierGate label="Version history"><DocumentLineage /></RestrictedTierGate>} />
        <Route path="lcaef" element={<RestrictedTierGate label="The LCAEF campaign"><LcaefRoute /></RestrictedTierGate>} />
        <Route path="alumni"     element={<AlumniReconciliation />} />
        <Route path="pipeline"   element={<DonorPipeline />} />
        <Route path="participate-leads" element={<ParticipateLeads />} />
        {/* CHAP-593 - the Discovery sender console. The server gates every
            call (admin role, DISCOVERY_SENDERS, no impersonation); a caller
            it refuses sees a one-line "senders only" message. */}
        <Route path="discovery"  element={<Discovery />} />
        <Route path="cabinet"    element={<Cabinet />} />
        <Route path="meetings"   element={<Meetings />} />
        <Route path="dataroom"   element={<DataRoom />} />
        <Route path="feedback"   element={<Feedback />} />
        <Route path="settings"   element={<TenantSettings />} />
      </Route>

      {/* Admin routes (cross-tenant) */}
      <Route path="/admin" element={<Layout variant="admin" />}>
        <Route index                        element={<AdminMetrics />} />
        <Route path="metrics"               element={<AdminMetrics />} />
        <Route path="tenants"               element={<ComingSoon title="Tenants"               ticket="CHAP-172" description="List + create + drill into individual tenants." />} />
        <Route path="tenants/new"           element={<AdminTenantNew />} />
        <Route path="users"                 element={<AdminUsers />} />
        <Route path="audit-events"          element={<AdminAuditLog />} />
        <Route path="health"                element={<AdminHealth />} />
        <Route path="billing"               element={<AdminBilling />} />
        <Route path="feature-flags"         element={<AdminFeatureFlags />} />
        <Route path="data-room/queue"       element={<AdminDataRoomQueue />} />
      </Route>
      </Route>

      {/* Legal pages (un-gated; visible to anonymous visitors per CHAP-135/134) */}
      <Route path="/legal/privacy" element={<Privacy />} />
      <Route path="/legal/terms"   element={<Terms />} />
      <Route path="/legal/cookies" element={<Cookies />} />

      {/* 404 catchall */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
